import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AUTHENTICATION_LOGIN_SERVICE,
  AUTHENTICATION_SUCCESS,
  AUTHENTICATION_UPDATE_CREDENTIAL,
  SERVICE_FAILURE,
  AUTHENTICATION_CREDENTIAL_RESET,
  AUTHENTICATION_RESET_ERROR,
  MODULE_FIND_ALL_SUCCESS,
  AUTHENTICATION_LOGIN_SERVICE_SUCCESS,
  REDIRECT_TO,
  AUTHENTICATION_CHANGE_PASSWORD_SUCCESS,
  AUTHENTICATION_RESET_CHANGE_PASSWORD_FORM,
  NOTIFICATION_SHOW,
  AUTHENTICATION_CHANGE_PASSWORD_SERVICE,
  AUTHENTICATION_UPDATE_LOADING,
  AUTHENTICATION_RESET_CHANGE_PASSWORD,
  AUTHENTICATION_FORGOT_PASSWORD_SERVICE,
  AUTHENTICATION_FORGOT_PASSWORD_SUCCESS,
  AUTHENTICATION_RESET_FORGOT_PASSWORD_FORM,
  AUTHENTICATION_RESET_FORGOT_PASSWORD,
} from '@store/actions';
import { AuthService } from '@core/services/auth/auth.service';
import { forkJoin, of } from 'rxjs';
import {
  selectAuthenticationForm,
  selectChangePasswordForm,
  selectForgotPasswordForm,
} from '@store/selectors/authentication.selectors';
import { Authentication } from '@core/models/authentication/authentication';
import { Router } from '@angular/router';
import { ModuleService } from '@core/services';
import { RolModuleEnum } from '@core/enums';
import { getEnumKeyByValue } from '@core/helpers';
import { ChangePassword } from '@core/services/auth/interfaces/reset-password';
import { ResetPasswordForm } from '@core/models';

@Injectable()
export class AuthenticationEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private authService: AuthService,
    private moduleService: ModuleService,
  ) {}

  logIn = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTHENTICATION_LOGIN_SERVICE),
      withLatestFrom(this.store.select(selectAuthenticationForm)),
      mergeMap(([_, authenticationFormState]) => {
        if (!authenticationFormState)
          return [
            AUTHENTICATION_LOGIN_SERVICE_SUCCESS({ loading: false })
          ];

        return this.authService.authenticate(authenticationFormState!).pipe(
          switchMap(({ data }) => {
            const authentication = data as Authentication;
            
            this.store.dispatch(AUTHENTICATION_SUCCESS({ authentication }));
            this.store.dispatch(AUTHENTICATION_UPDATE_CREDENTIAL({
              credential: {
                token: `Bearer ${authentication?.token}`,
                isSuperUser: authentication.is_superusuario,
                isReseller: authentication.esReseller,
                resellerId: !!authentication.esReseller ? authentication.id : (!authentication.is_superusuario ? authentication?.resellerId : undefined),
                resellerRoleId: !!authentication.esReseller ? authentication?.resellerRoleId : (!authentication.is_superusuario ? authentication?.resellerRoleId : undefined),
                cargoId: authentication.cargoId,
                permissions: authentication.permissions,
                nombre: authentication.nombreCompleto,
              }
            }));

            return forkJoin([
              this.moduleService.findAll()
                .pipe(
                  map(({ data }) => ({
                    data: [
                      ...data,
                      { idModulo: crypto.randomUUID(), nombre: getEnumKeyByValue(RolModuleEnum, RolModuleEnum.Reseller) as string, codigo: RolModuleEnum.Reseller as string, concesiones: [] },
                      { idModulo: crypto.randomUUID(), nombre: getEnumKeyByValue(RolModuleEnum, RolModuleEnum.Apk) as string, codigo: RolModuleEnum.Apk as string, concesiones: [] },
                    ]
                  }))
                ),
            ]).pipe(
              switchMap(([ { data: modules }]) => {
                this.store.dispatch(MODULE_FIND_ALL_SUCCESS({ moduleList: modules }));
                return forkJoin([this.moduleService.generatePermissions(authentication?.permissions)])
              }),
              switchMap(([permissions]) => {
                return [
                  AUTHENTICATION_LOGIN_SERVICE_SUCCESS({ loading: false }),
                  AUTHENTICATION_UPDATE_CREDENTIAL({ credential: { granted: true, permissions }}),
                  REDIRECT_TO({ path: this.authService.getDefaultPage() }),
                ];
              })
            )
          }),
          catchError((error) =>
            of(error).pipe(
              mergeMap((error) => {
                // eslint-disable-next-line no-console
                console.log(error)
                
                return [
                  SERVICE_FAILURE({ error }),
                  AUTHENTICATION_CREDENTIAL_RESET(),
                  AUTHENTICATION_RESET_ERROR(),
                ];
              })
            )
          )
        );
      })
    )
  );

  forgotPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTHENTICATION_FORGOT_PASSWORD_SERVICE),
      withLatestFrom(this.store.select(selectForgotPasswordForm)),
      filter(([_, forgotPasswordForm]) => !!forgotPasswordForm),
      mergeMap(([_, forgotPasswordForm]) => {
        this.store.dispatch(AUTHENTICATION_UPDATE_LOADING({ loading: true }));

        return this.authService.forgotPassword(forgotPasswordForm as ResetPasswordForm).pipe(
          mergeMap((response) => {
            const { message } = response;
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: message } }),
              AUTHENTICATION_UPDATE_LOADING({ loading: false }),
              AUTHENTICATION_FORGOT_PASSWORD_SUCCESS({ forgotPassword: response }),
              AUTHENTICATION_RESET_FORGOT_PASSWORD_FORM(),
              AUTHENTICATION_RESET_FORGOT_PASSWORD(),
            ];
          }),
          catchError((error) =>
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUTHENTICATION_CREDENTIAL_RESET(),
                  AUTHENTICATION_RESET_ERROR(),
                ];
              })
            )
          )
        );
      })
    )
  );

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTHENTICATION_CHANGE_PASSWORD_SERVICE),
      withLatestFrom(this.store.select(selectChangePasswordForm)),
      filter(([_, forgotPasswordForm]) => !!forgotPasswordForm),
      mergeMap(([_, changePasswordForm]) => {
        this.store.dispatch(AUTHENTICATION_UPDATE_LOADING({ loading: true }));

        return this.authService.changePassword(changePasswordForm as ChangePassword).pipe(
          mergeMap((response) => {
            const { message } = response;
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: message } }),
              AUTHENTICATION_UPDATE_LOADING({ loading: false }),
              AUTHENTICATION_CHANGE_PASSWORD_SUCCESS({ changePassword: response }),
              AUTHENTICATION_RESET_CHANGE_PASSWORD_FORM(),
              AUTHENTICATION_RESET_CHANGE_PASSWORD(),
            ];
          }),
          catchError((error) =>
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  AUTHENTICATION_CREDENTIAL_RESET(),
                  AUTHENTICATION_RESET_ERROR(),
                ];
              })
            )
          )
        );
      })
    )
  );
}
